import { API_URL } from "config";
import type { AppDispatch } from "features/store";
import { tokenRefreshed } from "features/auth/authSlice";

/**
 * The one place that refreshes the session (CAL-5974).
 *
 * The refresh token is single-use: rotating it blacklists the string that was
 * presented. The app used to refresh from five places that could not see each
 * other, and two tabs share one localStorage, so the same string went out twice
 * and the loser's 401 was read as a dead session - taking every tab with it.
 *
 * Every caller now comes through here, and here holds a cross-tab lock and
 * re-reads storage under it, so a tab that lost the race adopts the winner's
 * tokens instead of spending its own.
 *
 * The POST goes out on a plain fetch rather than the RTK base query. That is
 * deliberate: it needs no Authorization header, and it keeps a refresh from
 * ever re-entering the 401 handling that asked for it.
 */

const LOCK_NAME = "calibrate.token-refresh";
const REFRESH_PATH = "/auth/token/refresh/";

// Refresh this long before the access token actually expires, so a request is
// never sent with a token that dies in flight.
export const ACCESS_REFRESH_LEEWAY_SEC = 300;

export type RefreshOutcome =
  // Went to the server and got a new pair.
  | "refreshed"
  // Someone else had already refreshed; the stored token is good.
  | "already-fresh"
  // The server refused and nobody had rotated our token: the session is over.
  | "dead"
  // Network or server-side failure. The session is untouched - do NOT log out.
  | "transient";

export function getJwtExpMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4;
    const padded = pad ? payload + "=".repeat(4 - pad) : payload;
    const json = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof json.exp !== "number") return null;
    return json.exp * 1000;
  } catch {
    return null;
  }
}

export function readStoredAccessToken(): string | null {
  return localStorage.getItem("accessToken");
}

export function readStoredRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

/** True while the token has more than the leeway left on it. */
export function isAccessTokenFresh(token: string | null): boolean {
  if (!token) return false;
  const expMs = getJwtExpMs(token);
  // An unreadable token is treated as spent, not as fresh.
  if (expMs === null) return false;
  return Date.now() < expMs - ACCESS_REFRESH_LEEWAY_SEC * 1000;
}

/** Milliseconds until the stored token needs replacing; 0 when it already does. */
export function msUntilRefreshDue(): number {
  const expMs = getJwtExpMs(readStoredAccessToken() ?? "");
  if (expMs === null) return 0;
  return Math.max(0, expMs - ACCESS_REFRESH_LEEWAY_SEC * 1000 - Date.now());
}

function refreshUrl(): string {
  return `${API_URL.replace(/\/+$/, "")}${REFRESH_PATH}`;
}

type PostResult =
  | { kind: "ok"; access: string; refresh?: string }
  | { kind: "unauthorized" }
  | { kind: "transient" };

async function postRefresh(refresh: string): Promise<PostResult> {
  let response: Response;
  try {
    response = await fetch(refreshUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
  } catch {
    // Offline, DNS, aborted: the session is fine, the network is not.
    return { kind: "transient" };
  }

  if (response.status === 401) return { kind: "unauthorized" };
  if (!response.ok) return { kind: "transient" };

  try {
    const body = (await response.json()) as { access?: string; refresh?: string };
    if (typeof body.access !== "string") return { kind: "transient" };
    return { kind: "ok", access: body.access, refresh: body.refresh };
  } catch {
    return { kind: "transient" };
  }
}

/** Put whatever storage currently holds into Redux, which is what sends headers. */
function adoptStoredTokens(dispatch: AppDispatch): void {
  const access = readStoredAccessToken();
  const refresh = readStoredRefreshToken();
  if (access && refresh) {
    dispatch(tokenRefreshed({ access, refresh }));
  }
}

function storeTokens(
  access: string,
  refresh: string | undefined,
  dispatch: AppDispatch,
): void {
  localStorage.setItem("accessToken", access);
  if (refresh) localStorage.setItem("refreshToken", refresh);
  const effectiveRefresh = refresh ?? readStoredRefreshToken() ?? "";
  dispatch(tokenRefreshed({ access, refresh: effectiveRefresh }));
}

async function refreshUnderLock(dispatch: AppDispatch): Promise<RefreshOutcome> {
  // Double-checked: another tab may have refreshed while we waited for the lock.
  // Spending our copy of the refresh token now would blacklist theirs.
  if (isAccessTokenFresh(readStoredAccessToken())) {
    adoptStoredTokens(dispatch);
    return "already-fresh";
  }

  const refreshToken = readStoredRefreshToken();
  if (!refreshToken) return "dead";

  const first = await postRefresh(refreshToken);
  if (first.kind === "ok") {
    storeTokens(first.access, first.refresh, dispatch);
    return "refreshed";
  }
  if (first.kind === "transient") return "transient";

  // A 401. The lock covers this browser profile only, so another profile or
  // device may have rotated the token between our read and our post.
  const current = readStoredRefreshToken();
  if (current && current !== refreshToken) {
    const second = await postRefresh(current);
    if (second.kind === "ok") {
      storeTokens(second.access, second.refresh, dispatch);
      return "refreshed";
    }
    if (second.kind === "transient") return "transient";
  }

  return "dead";
}

// Used only where the Web Locks API is missing: one refresh per tab at a time.
let inFlight: Promise<RefreshOutcome> | null = null;

export async function ensureFreshAccessToken(
  dispatch: AppDispatch,
): Promise<RefreshOutcome> {
  // Fast path: nothing to do, and no lock contention for the common case.
  if (isAccessTokenFresh(readStoredAccessToken())) {
    adoptStoredTokens(dispatch);
    return "already-fresh";
  }

  // Typed as always present, but absent outside a secure context.
  const locks: LockManager | undefined = navigator.locks;
  if (locks) {
    return locks.request(LOCK_NAME, () => refreshUnderLock(dispatch));
  }

  if (!inFlight) {
    inFlight = refreshUnderLock(dispatch).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
